import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAppVersionPolicy } from '../../api/endpoints/ops';
import { staticQueryPolicy } from '../../api/queryPolicies';
import type { AppVersionPolicyEntry } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { SectionCard } from '../../components/ui/SectionCard';
import { useRecoveryQuery as useQuery } from '../../components/ui/useRecoveryQuery';
import { useOpsUser } from '../ownership/opsUser';
import { AppUpdateDialog } from './AppUpdateDialog';
import { APP_VERSION_POLICY_KEY, formatUpdatedAt, useAppLabels } from './appUpdateLabels';
import styles from './AppUpdatesTab.module.css';

const isBlank = (e: AppVersionPolicyEntry) =>
  e.minSupportedVersion == null && e.latestVersion == null && e.storeUrl == null && e.releaseNotes.en == null && e.releaseNotes.hi == null;

/**
 * Settings → App updates (#338, dnm-work#170 C4): the minimum and latest build per app and platform.
 * Every ops user reads it; only an ops manager gets the Edit action (O1, rhino-api R44 enforces).
 */
export function AppUpdatesTab() {
  const { t } = useTranslation();
  const { opsUser, isManager } = useOpsUser();
  const labels = useAppLabels();
  const [editing, setEditing] = useState<AppVersionPolicyEntry | null>(null);
  const query = useQuery({
    queryKey: APP_VERSION_POLICY_KEY,
    queryFn: getAppVersionPolicy,
    ...staticQueryPolicy,
  });
  const rows = query.data ?? [];
  const notSet = <span className={styles.notSet}>{t('settings.appUpdates.notSet')}</span>;
  const text = (v: string | null) => (v ? v : notSet);

  const columns: Column<AppVersionPolicyEntry>[] = [
    {
      key: 'app',
      header: t('settings.appUpdates.colApp'),
      render: (e) => (
        <div className={styles.appCell}>
          <span className={styles.app}>{labels.app(e.app)}</span>
          <span className={styles.platform}>{labels.platform(e.platform)}</span>
        </div>
      ),
    },
    { key: 'minimum', header: t('settings.appUpdates.colMinimum'), render: (e) => <span className={styles.version}>{text(e.minSupportedVersion)}</span> },
    { key: 'latest', header: t('settings.appUpdates.colLatest'), render: (e) => <span className={styles.version}>{text(e.latestVersion)}</span> },
    {
      key: 'store',
      header: t('settings.appUpdates.colStoreLink'),
      render: (e) =>
        e.storeUrl ? (
          <span className={styles.link} title={e.storeUrl}>
            {e.storeUrl}
          </span>
        ) : (
          notSet
        ),
    },
    {
      key: 'notes',
      header: t('settings.appUpdates.colWhatsNew'),
      render: (e) =>
        e.releaseNotes.en || e.releaseNotes.hi ? (
          <div className={styles.notes}>
            {e.releaseNotes.en && (
              <p className={styles.note}>
                <span className={styles.lang}>{t('settings.appUpdates.noteEn')}</span> {e.releaseNotes.en}
              </p>
            )}
            {e.releaseNotes.hi && (
              <p className={styles.note} lang="hi">
                <span className={styles.lang}>{t('settings.appUpdates.noteHi')}</span> {e.releaseNotes.hi}
              </p>
            )}
          </div>
        ) : (
          notSet
        ),
    },
    {
      key: 'updated',
      header: t('settings.appUpdates.colUpdated'),
      render: (e) =>
        e.updatedAt ? (
          <div className={styles.updated}>
            <span>{formatUpdatedAt(e.updatedAt)}</span>
            {e.updatedByName && <span className={styles.by}>{t('settings.appUpdates.updatedBy', { name: e.updatedByName })}</span>}
          </div>
        ) : (
          notSet
        ),
    },
  ];
  if (isManager) {
    columns.push({
      key: 'actions',
      header: t('settings.appUpdates.colActions'),
      render: (e) => (
        <Button
          variant="secondary"
          size="sm"
          aria-label={t('settings.appUpdates.editRow', { app: labels.app(e.app), platform: labels.platform(e.platform) })}
          onClick={() => setEditing(e)}
        >
          {t('settings.appUpdates.edit')}
        </Button>
      ),
    });
  }

  return (
    <SectionCard title={t('settings.appUpdates.title')}>
      <p className={styles.hint}>{t('settings.appUpdates.hint')}</p>
      {rows.length > 0 && rows.every(isBlank) && <p className={styles.empty}>{t('settings.appUpdates.emptyHint')}</p>}
      {opsUser && !isManager && <p className={styles.readOnly}>{t('settings.appUpdates.managersOnly')}</p>}
      <DataTable
        columns={columns}
        rows={rows}
        keyFn={(e) => `${e.app}/${e.platform}`}
        loading={query.isLoading}
        error={query.isError ? t('settings.appUpdates.loadError') : undefined}
        onRetry={() => query.refetch()}
      />
      {editing && isManager && <AppUpdateDialog entry={editing} onClose={() => setEditing(null)} />}
    </SectionCard>
  );
}
